import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePagination } from '../hooks/usePagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import { Plus, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { fetchClients, createClient } from '../services/clientService';
import { formatDate } from '../utils/dateUtils';
import logger from '../utils/logger';


export default function IntegrationsPage() {
  usePageTitle('Integrations');
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination - using custom hook for URL-synced pagination
  const {
    currentPage,
    totalPages,
    pageSize,
    displayedTotalCount,
    setCurrentPage,
    updatePaginationFromResponse,
    resetToFirstPage,
    showPagination,
    hasPreviousPage,
    hasNextPage,
    getDisplayRange,
  } = usePagination({ pageSize: 10 });

  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch clients on mount and when pagination/sorting changes
  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortField, sortDirection]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
      const response = await fetchClients({
        page: currentPage,
        page_size: pageSize,
        ordering: ordering,
        is_active: true,
      });

      // Handle both paginated and non-paginated responses
      if (response.results) {
        setClients(response.results);
        updatePaginationFromResponse(response.count || 0);
      } else if (Array.isArray(response)) {
        setClients(response);
        updatePaginationFromResponse(response.length);
      } else {
        // eslint-disable-next-line
        logger.error('Unexpected response format:', response);
      }
    } catch (err) {
      logger.error('Error loading clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    resetToFirstPage();
  };

  const handleAddClient = async (e) => {
    e.preventDefault();

    if (!newClientName.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await createClient({
        name: newClientName.trim(),
        is_active: true,
      });

      // Reset form and close dialog
      setNewClientName('');
      setIsAddDialogOpen(false);

      // Reload clients
      loadClients();
    } catch (err) {
      logger.error('Error creating client:', err);
      alert(`Failed to create client: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoadStatus = (loadStats) => {
    if (!loadStats || loadStats.total === 0) {
      return <span className="text-muted-foreground text-sm">-</span>;
    }

    const { successful, total } = loadStats;
    const successRate = total > 0 ? (successful / total) * 100 : 0;

    let bgColorClass = 'bg-green-100';
    let textColorClass = 'text-green-800';
    if (successRate < 100) {
      bgColorClass = 'bg-red-100';
      textColorClass = 'text-red-800';
    }

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${bgColorClass} ${textColorClass}`}>
        ● {successful} of {total} loads
      </span>
    );
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ChevronDown className="ml-1 h-4 w-4 inline" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Client Integrations</h1>
          <p className="text-muted-foreground">
            Data Integration Management
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Client List Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-destructive">Error: {error}</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">No clients found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort('name')}
                    >
                      CLIENT <SortIcon field="name" />
                    </TableHead>
                    <TableHead>INTEGRATIONS</TableHead>
                    <TableHead>LOAD STATUS</TableHead>
                    <TableHead>LAST ACTIVITY</TableHead>
                    <TableHead>ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id}>
                      {/* CLIENT */}
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell className="py-4">
                        {client.integrations && client.integrations.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-md">
                            {client.integrations
                              .filter((integration) => integration.display_name && integration.display_name.trim())
                              .map((integration) => (
                                <Badge key={integration.id} variant="secondary" className="text-xs">
                                  {integration.display_name}
                                </Badge>
                              ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {renderLoadStatus(client.load_stats)}
                      </TableCell>
                      <TableCell>
                        {client.last_activity ? (
                          <span className="text-sm">{formatDate(client.last_activity)}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/integrations/${client.id}`)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {showPagination && (
                <div className="flex items-center justify-between border-t px-6 py-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {getDisplayRange().start}-{getDisplayRange().end} of {displayedTotalCount} clients
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!hasPreviousPage || loading}
                    >
                      Previous
                    </Button>
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            disabled={loading}
                          >
                            {page}
                          </Button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2">...</span>;
                      }
                      return null;
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!hasNextPage || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent onClose={() => setIsAddDialogOpen(false)}>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client to manage integrations for.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddClient}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="clientName" className="text-sm font-medium">
                  Client Name
                </label>
                <Input
                  id="clientName"
                  placeholder="Enter client name"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !newClientName.trim()}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Client'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
